import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Phone, // Uncommented - adding phone support back
  Mail,
  Clock,
  MessageSquare,
  Send,
  Calendar,
  User,
  Building,
  HeadphonesIcon, // Uncommented - adding phone support back
  CheckCircle,
  Star,
  CreditCard,
  TrendingUp,
  UserPlus
} from 'lucide-react';

const Contact = () => {
  const [appointmentType, setAppointmentType] = useState('general');

  const contactMethods = [
    {
      icon: Mail,
      title: 'Email Support',
      primary: 'info@firstheritageinc.com',
      secondary: 'Email support only',
      description: 'Send us your questions anytime',
      availability: 'Response within 24 hours',
      bgColor: 'from-blue-500 to-blue-600'
    },
    {
      icon: MessageSquare,
      title: 'Live Chat',
      primary: 'Available on our website',
      secondary: 'Instant messaging support',
      description: 'Get immediate assistance',
      availability: '24/7 Online Chat',
      bgColor: 'from-green-500 to-green-600'
    },
    {
      icon: MessageSquare,
      title: 'Live Chat',
      primary: 'Available on our website',
      secondary: 'Instant messaging support',
      description: 'Get immediate assistance',
      availability: '24/7 Online Chat',
      bgColor: 'from-purple-500 to-purple-600'
    },
    // Commented out video call method (which often involves phone numbers)
    // {
    //   icon: HeadphonesIcon,
    //   title: 'Video Call',
    //   primary: 'Schedule virtual meeting',
    //   secondary: 'Face-to-face consultation',
    //   description: 'Personal banking advisor',
    //   availability: 'Mon-Fri: 9AM-5PM',
    //   bgColor: 'from-orange-500 to-orange-600'
    // }
  ];

  const appointmentTypes = [
    { value: 'general', label: 'General Banking', icon: Building },
    { value: 'loans', label: 'Loans & Mortgages', icon: CreditCard },
    { value: 'business', label: 'Business Banking', icon: Building },
    { value: 'investment', label: 'Investment Services', icon: TrendingUp },
    { value: 'support', label: 'Account Support', icon: MessageSquare } // Changed from HeadphonesIcon to MessageSquare
  ];

  const supportFeatures = [
    {
      icon: Clock,
      title: '24/7 Availability',
      description: 'Round-the-clock email and chat support'
    },
    {
      icon: User,
      title: 'Personal Banker',
      description: 'Dedicated relationship manager via email'
    },
    {
      icon: MessageSquare,
      title: 'Multi-Channel Support',
      description: 'Email, chat, and online assistance'
    },
    {
      icon: CheckCircle,
      title: 'Quick Resolution',
      description: 'Fast and efficient problem-solving via email'
    }
  ];

  // Handle form submission to email
  const handleFormSubmit = (e) => {
    e.preventDefault();
    // This would typically send form data to the company email
    // For now, we'll just show a success message
              alert('Your message has been sent to our support team at info@firstheritageinc.com');
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-900/90 to-primary-800/90"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-6">
              Get in Touch
              <span className="block text-3xl md:text-5xl bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent">
                We're Here to Help
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-blue-100 leading-relaxed max-w-3xl mx-auto mb-8">
              Have questions? Need assistance? Our friendly team is ready to help you
              with all your banking needs, 24 hours a day.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-primary-900 bg-white rounded-xl hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <UserPlus className="w-6 h-6 mr-2" />
                Open account
              </Link>
              <a
                href="#schedule-appointment"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white border-2 border-white rounded-xl hover:bg-white hover:text-primary-900 transition-all duration-300"
              >
                <Calendar className="w-6 h-6 mr-2" />
                Schedule Appointment
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Methods Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Multiple Ways to Connect
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the contact method that works best for you. We're available through multiple channels to provide the support you need.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactMethods.map((method, index) => (
              <motion.div
                key={method.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center group"
              >
                <div className={`w-16 h-16 bg-gradient-to-r ${method.bgColor} rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <method.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{method.title}</h3>
                <p className="text-primary-600 font-semibold mb-1">{method.primary}</p>
                <p className="text-gray-600 mb-3">{method.secondary}</p>
                <p className="text-gray-700 mb-2">{method.description}</p>
                <span className="inline-block bg-primary-100 text-primary-600 px-3 py-1 rounded-full text-sm font-medium">
                  {method.availability}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Support Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Customer Support
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our dedicated support team is committed to providing exceptional service and resolving your banking needs quickly and efficiently.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {supportFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-gray-50 rounded-2xl p-8 text-center"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Need Immediate Assistance?</h3>
            <p className="text-gray-600 mb-6">Our customer service team is available 24/7 via email and chat to help with your banking needs.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:info@firstheritageinc.com"
                className="inline-flex items-center justify-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Mail className="w-5 h-5 mr-2" />
                Email Support
              </a>
              <Link
                to="/support"
                className="inline-flex items-center justify-center px-6 py-3 border border-primary-600 text-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
              >
                <MessageSquare className="w-5 h-5 mr-2" />
                Online Support Center
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Schedule Appointment Section */}
      <section id="schedule-appointment" className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Schedule an Appointment
            </h2>
            <p className="text-xl text-gray-600">
              Meet with one of our banking professionals to discuss your financial needs and explore personalized solutions.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl p-8 shadow-lg"
          >
            <form className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                    placeholder="John"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                    placeholder="john@example.com"
                  />
                </div>
                {/* Commented out phone number field - using email only */}
                {/*
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                    placeholder="+1 (281) 317-3649"
                  />
                </div>
                */}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Appointment Type
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {appointmentTypes.map((type) => (
                    <label
                      key={type.value}
                      className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        appointmentType === type.value
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="appointmentType"
                        value={type.value}
                        checked={appointmentType === type.value}
                        onChange={(e) => setAppointmentType(e.target.value)}
                        className="sr-only"
                      />
                      <type.icon className={`w-5 h-5 mr-3 ${
                        appointmentType === type.value ? 'text-primary-600' : 'text-gray-400'
                      }`} />
                      <span className={`font-medium ${
                        appointmentType === type.value ? 'text-primary-700' : 'text-gray-700'
                      }`}>
                        {type.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Date
                  </label>
                  <input
                    type="date"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Time
                  </label>
                  <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors">
                    <option>9:00 AM</option>
                    <option>10:00 AM</option>
                    <option>11:00 AM</option>
                    <option>12:00 PM</option>
                    <option>1:00 PM</option>
                    <option>2:00 PM</option>
                    <option>3:00 PM</option>
                    <option>4:00 PM</option>
                    <option>5:00 PM</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meeting Preference
                </label>
                <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors">
                  <option>Virtual Meeting (Video Call)</option>
                  {/* Commented out phone consultation - using email only */}
                  {/* <option>Phone Consultation</option> */}
                  <option>In-Person Meeting</option>
                  <option>Email Consultation</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes
                </label>
                <textarea
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                  placeholder="Please let us know how we can help you today..."
                ></textarea>
              </div>

              <button
                type="submit"
                onClick={handleFormSubmit}
                className="w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white py-4 px-6 rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all font-semibold shadow-lg hover:shadow-xl"
              >
                <Calendar className="w-5 h-5 mr-2 inline" />
                Submit Request (Email will be sent to admin@firstcapitalsint.com)
              </button>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Quick Contact Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Send us a Message</h2>
              <form className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                    placeholder="john@example.com"
                  />
                </div>

                {/* Commented out phone number field - using email only */}
                {/*
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                    placeholder="+1 (281) 317-3649"
                  />
                </div>
                */}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject
                  </label>
                  <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors">
                    <option>General Inquiry</option>
                    <option>Account Support</option>
                    <option>Loan Information</option>
                    <option>Business Banking</option>
                    <option>Technical Support</option>
                    <option>Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                    placeholder="How can we help you today?"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white py-4 px-6 rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all font-semibold shadow-lg hover:shadow-xl"
                >
                  <Send className="w-5 h-5 mr-2 inline" />
                  Send Message (to admin@firstcapitalsint.com)
                </button>
              </form>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Contact Information</h2>

              <div className="space-y-8">
                {/* Commented out phone contact information - using email only */}
                {/*
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Phone</h3>
                    <p className="text-gray-600">Main: +1 (281) 317-3649</p>
                    <p className="text-gray-600">Customer Service: +1 (281) 317-3649</p>
                    <p className="text-gray-600">Loan Department: +1 (281) 317-3649</p>
                  </div>
                </div>
                */}

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Email</h3>
                                    <p className="text-gray-600">admin@firstcapitalsint.com</p>
                <p className="text-gray-600">support@firstcapitalsint.com</p>
                <p className="text-gray-600">loans@firstcapitalsint.com</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Customer Service Hours</h3>
                    <p className="text-gray-600">Email Support: Always Available</p>
                    <p className="text-gray-600">Online Banking: Always Available</p>
                    <p className="text-gray-600">Email Response: Within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Digital Support</h3>
                    <p className="text-gray-600">Live Chat: Available 24/7</p>
                    <p className="text-gray-600">Mobile App Support</p>
                    <p className="text-gray-600">Online Help Center</p>
                  </div>
                </div>
              </div>

              {/* Commented out emergency contact with phone - using email only */}
              {/*
              <div className="mt-8 p-6 bg-gray-50 rounded-xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Emergency Contact</h3>
                <p className="text-gray-600 mb-2">Lost or stolen cards:</p>
                <p className="text-primary-600 font-semibold">+1 (281) 317-3649</p>
                <p className="text-gray-600 text-sm mt-2">Available 24/7 for immediate card blocking</p>
              </div>
              */}

              <div className="mt-8 p-6 bg-gray-50 rounded-xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Emergency Support</h3>
                <p className="text-gray-600 mb-2">Lost or stolen cards:</p>
                <p className="text-primary-600 font-semibold">support@firstcapitalsint.com</p>
                <p className="text-gray-600 text-sm mt-2">Email us immediately for card blocking assistance</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact; 